import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import type { CarFleetViewRecord } from '@navio/nsdk';
import { invalidateQuery, noddiAsync, URLKeys } from 'noddi-async';
import { NoddiBadge, NoddiButton, NoddiDialog } from 'noddi-ui-web';
import { formatLicensePlate, getCarDisplayName } from 'noddi-util';
import { VehicleCategoryIcon } from 'noddi-web';
import { useState } from 'react';
import { ConfirmPromptFooter } from './ConfirmPromptFooter';
import { InlineRemovalConfirm } from './InlineRemovalConfirm';
import { isCarOnTireHotel } from './isCarOnTireHotel';
import { PERSONAL_INFO_REVIEW_MUTATION_KEY } from './types';
import { useReviewMutationsPending } from './useReviewMutationsPending';

type Props = {
  userGroupId: number;
  cars: CarFleetViewRecord[];
  onConfirmed: () => void;
  onSkip: () => void;
};

/**
 * "Are these still your cars?" Removal ends the ownership straight away — nothing here is held back
 * for the final Confirm, so a Skip on a later dialog cannot throw a removal away.
 */
export const ConfirmCarsDialog = ({ userGroupId, cars, onConfirmed, onSkip }: Props) => {
  const [carIdPendingRemoval, setCarIdPendingRemoval] = useState<number | null>(null);

  // Only this flow's requests, not every mutation in the customer app, so Confirm cannot move on
  // over a half-applied removal without unrelated work disabling the prompt.
  const isMutating = useReviewMutationsPending();

  const {
    mutate: removeCars,
    isPending: isRemoving,
    error: removeError
  } = noddiAsync.usePatch({
    type: URLKeys.patchRemoveCarsFromUserGroup,
    queryConfig: {
      mutationKey: PERSONAL_INFO_REVIEW_MUTATION_KEY,
      // Await the refetch before clearing: dropping the pending state first leaves the removed row
      // on screen, removable a second time.
      onSuccess: async () => {
        await invalidateQuery({ urlKey: URLKeys.getPaginatedFleetViewCars });
        invalidateQuery({ urlKey: URLKeys.getPaginatedUserGroupCarsForBooking });
        setCarIdPendingRemoval(null);
      }
    }
  });

  return (
    <NoddiDialog
      open
      // Answered with Confirm or Skip — never by clicking outside the prompt.
      onClose={() => {}}
      showCloseButton={false}
      className='max-h-[85vh]'
      title={t`Are these still your cars?`}
      description={t`Remove any car you no longer own, so it stops showing up when you book.`}
      footer={
        <ConfirmPromptFooter
          isBusy={isMutating}
          onSkip={onSkip}
          primaryAction={
            <NoddiButton disabled={isMutating} onClick={onConfirmed}>
              {t`Confirm`}
            </NoddiButton>
          }
        />
      }
    >
      <div className='divide-y divide-border overflow-hidden rounded-lg border border-border'>
        {cars.map((car) => {
          const isPendingRemoval = carIdPendingRemoval === car.id;
          const isOnTireHotel = isCarOnTireHotel(car);

          return (
            <div key={car.id} className='flex flex-col gap-2 p-4'>
              <div className='flex items-start justify-between gap-3'>
                <div className='flex min-w-0 items-baseline gap-2'>
                  <VehicleCategoryIcon vehicleCategory={car.vehicleCategory} size='small' />
                  <p className='min-w-0'>
                    <span className='font-semibold'>
                      {getCarDisplayName({ car, showLicensePlate: false, showYear: false })}
                    </span>{' '}
                    <span className='text-muted-foreground'>{formatLicensePlate(car.licensePlate.number)}</span>
                  </p>
                </div>
                {isOnTireHotel ? (
                  <NoddiBadge variant='success' className='shrink-0'>
                    <Trans>On tire hotel</Trans>
                  </NoddiBadge>
                ) : (
                  !isPendingRemoval && (
                    <NoddiButton
                      className='shrink-0'
                      variant='ghost'
                      size='small'
                      startIcon='Trash2'
                      aria-label={`${t`Remove`}: ${getCarDisplayName({ car, showLicensePlate: true, showYear: false })}`}
                      onClick={() => setCarIdPendingRemoval(car.id)}
                    >
                      {t`Remove`}
                    </NoddiButton>
                  )
                )}
              </div>

              {isPendingRemoval && (
                <InlineRemovalConfirm
                  question={t`Remove this car?`}
                  error={removeError}
                  isRemoving={isRemoving}
                  onCancel={() => setCarIdPendingRemoval(null)}
                  onConfirm={() => removeCars({ userGroupId, carIds: [car.id] })}
                />
              )}
            </div>
          );
        })}
      </div>
    </NoddiDialog>
  );
};
